import { useEffect, useState } from 'react'

import { Button } from './button'
import { Modal } from './modal'
import { t } from '../copy'
import { T } from '../copy/text'
import {
  useMe,
  useRequestStepUpCode,
  useStepUpWithPassword,
  useVerifyStepUpCode,
} from '../app/queries'
import type { Schemas } from '@npc/api-client'

/**
 * 「确认一下是你本人」那个框。**所有红级动作共用这一份。**
 *
 * ------------------------------------------------------------------
 * # 这个组件存在之前，界面上根本没有二次验证这一步
 *
 * 服务端 2026-08 就把三条换令牌的路修好了（`/auth/step-up/*`），
 * 而控制台**一处都没调过**：碰到 `step_up_required` 只是把那句话显示出来，
 * 用户看完既没有输入框也没有按钮。于是「删机器、看原始密钥、改团队权限、
 * 批红级」这四件事在界面上是**走不通**的——不是被拦住，是没有路。
 *
 * 唯一走通过的是静态站点发布，它在自己那张表单里内联了一个密码框：
 * 一份写死只认密码的实现，账号没设密码的人在那儿转不出去。
 *
 * # 用哪几种因子不在这里判
 *
 * `/auth/me` 的 `step_up_methods` 说了算，唯一说法是服务端的
 * `auth.StepUpMethods`（CLAUDE.md §10）。**不要拿 `sign_in_paths` 推**：
 * 那是「他还进不进得来」，这是「他能不能当场再验一次」。
 * 两份判据分家的那天，界面会给出一个他按不下去的按钮——
 * 2026-08-25 之前那正是发生过的事（没指纹的人点了弹框、按不出来、
 * 又回到同一句话，转不出去）。
 *
 * 一种都没有时**不给按钮，给一句能自己走通的下一步**：这一屏底下就有
 * 设密码的表单。「请联系管理员」在这个产品里是句空话——他就是管理员。
 *
 * # 令牌绑到具体那一条上
 *
 * `resource` 必须是这次要动的那个对象的 id。服务端 2026-09-01 起
 * **逐字比对，空串不再是通配符**（`ConsumeStepUp`）——所以这里漏传
 * 会当场 403，而不是悄悄变成一张通吃令牌。
 * ------------------------------------------------------------------
 */
export interface StepUpDialogProps {
  open: boolean
  /** 这次要做的是哪一类危险动作。令牌只对声明的这一类生效。 */
  intent: Schemas['StepUpIntent']
  /** 这次要动的是哪一个对象。**不要留空。** */
  resource: string
  /** 标题。不传用「确认一下是你本人」。 */
  title?: string
  /** 说明。**传这次具体要干什么**，比通用那句有用得多。 */
  description?: string
  onCancel: () => void
  /** 验过了。参数是一次性令牌，调用方拿它放进 X-Npc-Step-Up 头。 */
  onVerified: (token: string) => void
}

type Method = 'password' | 'email_code'

export function StepUpDialog({
  open,
  intent,
  resource,
  title,
  description,
  onCancel,
  onVerified,
}: StepUpDialogProps) {
  const me = useMe()
  const password = useStepUpWithPassword()
  const requestCode = useRequestStepUpCode()
  const verifyCode = useVerifyStepUpCode()

  const [method, setMethod] = useState<Method | null>(null)
  const [secret, setSecret] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const methods = (me.data?.step_up_methods ?? []).filter(
    (m): m is Method => m === 'password' || m === 'email_code',
  )
  // 顺序由服务端定（稳定），第一个就是默认。自己再排一次会让默认因子
  // 每次刷新都变一个——那是 stepupmethods.go 特意排过序要避免的事。
  const active = method ?? methods[0] ?? null

  // 每次重新打开都从干净状态开始。**上一次的密码不许留在框里**：
  // 一个还挂着密码的框，下一次打开时按一下回车就过了，
  // 而「当场再验一次」要的正是那一下不能被省掉。
  useEffect(() => {
    if (!open) {
      setMethod(null)
      setSecret('')
      setSentTo(null)
      setFailure(null)
    }
  }, [open])

  const busy = password.isPending || verifyCode.isPending

  /**
   * 把令牌交出去。**空的不交。**
   *
   * 2026-09-06 生产上这里静静地交出了一个 undefined：服务端 `StepUpTicket`
   * 少了 json 标签，发出去的是 `{"Token":…}`，而契约与这里读的都是 `token`。
   * 于是调用方把 `X-Npc-Step-Up` 头空着发出去，服务端照规矩回
   * `step_up_required`——用户刚验完，界面转回来跟他说「得你当场再验一次」。
   * 改团队权限与邀请成员两条路都卡死在这个自己指向自己的圈里。
   *
   * 根因修在服务端（json 标签 + `TestStepUpWireShapeMatchesContract` 守着）。
   * 这一道是**说话的那一半**：真再出一次同形状的事，
   * 至少他看到的是一句认领，而不是一个把他指回原地的提示。
   *
   * 注意这不是「兜底放行」——它一样办不成事，只是把办不成的原因说对了。
   */
  function handOver(token: string | undefined): boolean {
    if (typeof token !== 'string' || token.trim() === '') {
      setFailure(t('account.stepup.no_ticket'))
      return false
    }
    onVerified(token)
    return true
  }

  async function submit() {
    setFailure(null)
    try {
      if (active === 'password') {
        const ticket = await password.mutateAsync({ password: secret, intent, resource })
        if (!handOver(ticket.token)) return
      } else if (active === 'email_code') {
        const ticket = await verifyCode.mutateAsync({ code: secret.trim(), intent, resource })
        if (!handOver(ticket.token)) return
      }
      setSecret('')
    } catch (error) {
      // 后端按契约给的是渲染好的人话，原样用它——前端再包一层就是两份真相。
      setFailure(error instanceof Error ? error.message : t('error.generic'))
    }
  }

  async function sendCode() {
    setFailure(null)
    try {
      const ticket = await requestCode.mutateAsync({ intent, resource })
      setSentTo(ticket.delivered_to ?? me.data?.email ?? '')
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t('error.generic'))
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      title={title ?? t('account.stepup.title')}
      description={description ?? t('account.stepup.body')}
      footer={
        active ? (
          <>
            <Button variant="ghost" onClick={onCancel} data-testid="step-up-cancel">
              <T id="account.stepup.cancel" />
            </Button>
            <Button
              variant="primary"
              disabled={busy || secret.trim().length === 0}
              onClick={() => void submit()}
              data-testid="step-up-confirm"
            >
              {busy ? <T id="account.stepup.working" /> : <T id="account.stepup.confirm" />}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onCancel} data-testid="step-up-cancel">
            <T id="account.stepup.cancel" />
          </Button>
        )
      }
    >
      {me.isPending ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid="step-up-loading">
          <T id="account.stepup.working" />
        </p>
      ) : active === null ? (
        <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft" data-testid="step-up-no-method">
          <T id="account.stepup.none" />
        </p>
      ) : (
        <form
          className="flex flex-col gap-npc-grid-sm"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          {active === 'email_code' && sentTo === null ? (
            <Button
              variant="secondary"
              onClick={() => void sendCode()}
              disabled={requestCode.isPending}
              data-testid="step-up-send-code"
            >
              <T id="account.stepup.code.send" />
            </Button>
          ) : null}

          {active === 'email_code' && sentTo !== null ? (
            <p className="m-0 text-npc-caption text-npc-ink-soft" data-testid="step-up-code-sent">
              <T id="account.stepup.code.sent" vars={{ email: sentTo }} />
            </p>
          ) : null}

          {active === 'password' || sentTo !== null ? (
            <label className="block">
              <span className="mb-[7px] block text-npc-caption text-npc-ink-soft">
                <T
                  id={active === 'password' ? 'account.stepup.password.label' : 'account.stepup.code.label'}
                />
              </span>
              <input
                type={active === 'password' ? 'password' : 'text'}
                value={secret}
                autoFocus
                onChange={(event) => setSecret(event.target.value)}
                autoComplete={active === 'password' ? 'current-password' : 'one-time-code'}
                inputMode={active === 'password' ? undefined : 'numeric'}
                maxLength={active === 'password' ? undefined : 6}
                data-testid="step-up-secret"
                className={
                  'block w-full rounded-npc-inset border border-npc-border bg-npc-panel px-[15px] py-npc-grid-sm ' +
                  'font-[inherit] text-npc-section-sm font-normal text-npc-ink placeholder:text-npc-ink-softer'
                }
              />
            </label>
          ) : null}

          {active === 'email_code' && sentTo !== null ? (
            <button
              type="button"
              onClick={() => void sendCode()}
              disabled={requestCode.isPending}
              data-testid="step-up-resend"
              className="cursor-pointer self-start border-none bg-transparent p-0 text-npc-caption text-npc-green underline"
            >
              <T id="account.stepup.code.resend" />
            </button>
          ) : null}

          {methods.length > 1 ? (
            <button
              type="button"
              data-testid="step-up-switch"
              onClick={() => {
                setMethod(active === 'password' ? 'email_code' : 'password')
                setSecret('')
                setSentTo(null)
                setFailure(null)
              }}
              className="cursor-pointer self-start border-none bg-transparent p-0 text-npc-caption text-npc-ink-soft underline"
            >
              <T
                id={active === 'password' ? 'account.stepup.method.email_code' : 'account.stepup.method.password'}
              />
            </button>
          ) : null}

          {failure ? (
            <p role="alert" className="m-0 text-npc-caption text-npc-red-ink" data-testid="step-up-error">
              {failure}
            </p>
          ) : null}
        </form>
      )}
    </Modal>
  )
}
